
import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import axios from "axios";
import 'react-toastify/dist/ReactToastify.css';
import { BaseUrl } from "../App";
import { Circles } from 'react-loader-spinner';
import { FadeLoader } from 'react-spinners';


const decodeBase64 = (str) => {
  try {
    return atob(str); // Decodes the Base64 string
  } catch (error) {
    console.error("Error decoding Base64:", error);
    return str; // Returns the original string if decoding fails
  }
};

const SMS_Template = () => {
  const [activeTab, setActiveTab] = useState("Client SMS Template");
  const [expandedSections, setExpandedSections] = useState({});
  const [enabledSections, setEnabledSections] = useState({});
  const [textareaContent, setTextareaContent] = useState({});
  const [templates, setTemplates] = useState([]);
  const [successMessage, setSuccessMessage] = useState("")
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const defaultSMSContent = `a`;

  const placeholders = [
    "booking_date", "booking_time", "service_name", "client_name", "method_name",
    "units", "addons", "firstname", "lastname", "client_email", "client_phone",
    "payment_method", "vaccum_cleaner_status", "parking_status", "notes",
    "contact_status", "price", "client_address", "client_city", "client_state",
    "client_zip", "client_promocode", "app_remain_time", "reject_status",
    "reject_reason", "business_logo", "admin_name", "company_name", "company_address",
    "company_city", "company_state", "company_zip", "company_country", "company_phone",
    "company_email"
  ];

  useEffect(() => {
    let timer;
    if (error) {
      timer = setTimeout(() => {
        setError("");
      }, 5000); // 5 seconds
    }
    return () => clearTimeout(timer);
  }, [error]);
  useEffect(() => {
    fetchTemplates(getUserType(activeTab));
  }, [activeTab]);

  const fetchTemplates = async (userType) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${BaseUrl}/backend/api/sms_template.php`,
        {
          action: "get_client_sms_templates",
          api_key: "1",
          user_type: userType,
        },
        {
          headers: {
            "Content-Type": "application/json",
          }
        }
      );

      const data = response.data;

      if (data.status === "true") {
        setTemplates(data.data);

        const initialContent = {};
        const initialEnabledSections = {};

        data.data.forEach((template) => {
          const decodedMessage = decodeBase64(template.default_message || "");
          initialContent[template.sms_subject] = template.sms_message || decodedMessage;
          initialEnabledSections[template.sms_subject] = template.sms_template_status === "E";
        });

        setTextareaContent(initialContent);
        setEnabledSections(initialEnabledSections);
      } else {
        toast.error("Failed to fetch templates");
      }
    } catch (error) {
      toast.error("Error fetching templates");
    }finally {
      setLoading(false);
    }
  };

  const updateTemplateStatus = async (id, status) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${BaseUrl}/backend/api/sms_template.php`,
        {
          action: 'update_sms_template_status',
          api_key: '1',
          id: id,
          sms_template_status: status ? 'E' : 'D',
        },
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      const data = response.data;

      if (data.status === "true") {
        setSuccessMessage("Update SMS Template Status Successfully!")
        toast.success('Template status updated successfully');
      } else {
        toast.error('Failed to update template status');
      }
    } catch (error) {
      toast.error('Error updating template status');
    }finally {
      setLoading(false);
    }
  };

  const updateTemplateMessage = async (id, message) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${BaseUrl}/backend/api/sms_template.php`,
        {
          action: 'update_sms_template',
          api_key: '1',
          id: id,
          sms_message: message,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      const data = response.data;

      if (data.status === "true") {
        setSuccessMessage("Update SMS Template Successfully!")
        toast.success('SMS template updated successfully');
      } else {
        toast.error('Failed to update SMS template');
      }
    } catch (error) {
      toast.error('Error updating SMS template');
    }finally {
      setLoading(false);
    }
  };

  const handleTextClick = (section, text) => {
    const newText = `[[${text}]]\n${textareaContent[section] || ""}`;
    setTextareaContent((prev) => ({
      ...prev,
      [section]: newText,
    }));
  };

  const handleDefaultTemplate = (section) => {
    setTextareaContent((prev) => ({
      ...prev,
      [section]: defaultSMSContent,
    }));
  };

  const handleSaveTemplate = (id, section) => {
    const message = textareaContent[section];
    updateTemplateMessage(id, message);
  };

  const toggleEnable = (section, id) => {
    const newStatus = !enabledSections[section];
    setEnabledSections((prev) => ({
      ...prev,
      [section]: newStatus,
    }));
    updateTemplateStatus(id, newStatus);
  };

  const toggleExpand = (section) => {
    setExpandedSections((prev) => {
      const newExpandedSections = {};
      Object.keys(prev).forEach(key => {
        newExpandedSections[key] = false;
      });
      newExpandedSections[section] = !prev[section];
      return newExpandedSections;
    });
  };

  const getUserType = (tab) => {
    switch (tab) {
      case "Client SMS Template":
        return 'C';
      case "Admin SMS Template":
        return 'A';
      case "Staff SMS Template":
        return 'S';
      default:
        return 'C';
    }
  };

  const renderSections = () => {
    return templates.map((template) => {
      const section = template.sms_subject;
      const templateContent = textareaContent[section] || defaultSMSContent;

      return (
        <div className="relative min-h-screen">
        {/* Loading Indicator */}
          {loading && (
               <div className="fixed inset-0 flex items-center justify-center z-50">
                 <div className="flex flex-col items-center p-3 bg-black border border-white rounded-2xl">
                   <FadeLoader 
                     height={15}
                     width={5}
                     color="#ffffff"
                     ariaLabel="loading"
                   />
                   <h1 className="mt-1 text-lg text-white">Please wait...</h1>
                 </div>
               </div>
             )}
             
        <div key={template.id} className="mb-6">
          {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{successMessage}</span>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
          <div className="flex justify-between items-center bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-lg shadow-sm border border-gray-200 xl:px-24">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => toggleEnable(section, template.id)}
                className={`relative w-14 h-7 rounded-full transition-all duration-300 ${
                  enabledSections[section] ? "bg-blue-500" : "bg-gray-300"
                }`}
              >
                <div
                  className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-all duration-300 ${
                    enabledSections[section] ? "right-1" : "left-1"
                  }`}
                />
              </button>
              <span className="font-semibold text-lg text-gray-800 break-words">
                {section}
              </span>
            </div>

            <button
              onClick={() => toggleExpand(section)}
              className="w-10 h-10 flex items-center justify-center bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 transition-all duration-200"
            >
              {expandedSections[section] ? (
                <ChevronUp className="w-5 h-5 text-gray-600" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-600" />
              )}
            </button>
          </div>

          <div
            className={`transform transition-all duration-300 ease-in-out overflow-hidden ${
              expandedSections[section]
                ? "max-h-[800px] opacity-100 mt-4"
                : "max-h-0 opacity-0"
            }`}
          >
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
              <div className="flex flex-col lg:flex-row gap-6">
                <div className="flex-1">
                  <textarea
                    className="w-full h-64 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none font-mono text-sm"
                    placeholder="Enter your template here..."
                    value={templateContent}
                    onChange={(e) => {
                      setTextareaContent((prev) => ({
                        ...prev,
                        [section]: e.target.value,
                      }));
                    }}
                  />
                </div>

                <div className="lg:w-64">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-700 mb-3">Placeholders</h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {placeholders.map((text) => (
                        <div
                          key={text}
                          onClick={() => handleTextClick(section, text)}
                          className="text-sm text-gray-600 p-2 rounded-md transition-colors duration-200 cursor-pointer hover:bg-blue-50"
                        >
                          [[{text}]]
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
l
              <div className="flex flex-wrap gap-3 mt-6">
                <button
                  onClick={() => handleSaveTemplate(template.id, section)}
                  className="px-6 py-2 rounded-lg shadow-sm transition-colors duration-200 bg-blue-500 text-white hover:bg-blue-600"
                >
                  Save Template
                </button>
                <button
                  onClick={() => handleDefaultTemplate(section)}
                  className="px-6 py-2 rounded-lg shadow-sm transition-colors duration-200 bg-gray-500 text-white hover:bg-gray-600"
                >
                  Default Template
                </button>
              </div>
            </div>
          </div>
        </div>
       
  </div>
      );
    });
  };
  

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      <div className="flex flex-nowrap justify-between border-b border-gray-300 pb-4 mb-8 overflow-x-auto">
        <div className="flex flex-row w-full gap-2">
          {["Client SMS Template", "Admin SMS Template", "Staff SMS Template"].map((tab) => (
            <button
              key={tab}
              className={`flex-1 text-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                activeTab === tab
                  ? "bg-blue-500 text-white shadow-md transform -translate-y-0.5"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        {renderSections()}
      </div>
    </div>
    
  );
};

export default SMS_Template;
